import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User, LogIn, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Add noindex meta tag and set title
  useEffect(() => {
    document.title = "Connexion - Espace Pro | Fast&Car";
    
    // Add noindex meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    
    return () => {
      // Remove noindex when leaving admin pages
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Trim any invisible whitespace that mobile keyboards may add
    const cleanUsername = username.trim();
    const cleanPassword = password.trim();

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: cleanUsername,
        password: cleanPassword,
      });

      if (response.data.success) {
        localStorage.setItem("adminToken", response.data.token);
        toast.success("Connexion réussie !");
        navigate("/espace-pro-fastandcar/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="pt-32 md:pt-40 min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md mx-4">
        <div className="bg-zinc-900/80 border border-zinc-800 p-8 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-neon-lime/10 mx-auto mb-4 flex items-center justify-center">
              <Lock className="w-10 h-10 text-neon-lime" />
            </div>
            <h1 className="font-unbounded text-2xl font-bold text-white">
              Administration
            </h1>
            <p className="text-zinc-400 mt-2 text-sm">
              Connectez-vous pour accéder au tableau de bord
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white flex items-center gap-2">
                <User className="w-4 h-4 text-neon-lime" />
                Identifiant
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Entrez votre identifiant"
                required
                data-testid="login-username"
                className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none focus:border-neon-lime"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-neon-lime" />
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  required
                  autoComplete="current-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="login-password"
                  className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none focus:border-neon-lime pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-neon-lime transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
            >
              <span className="flex items-center gap-2 justify-center">
                {loading ? (
                  "Connexion..."
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Se connecter
                  </>
                )}
              </span>
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-zinc-600 text-xs mt-8">
            Fast&Car - Administration sécurisée
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
